import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { fmtIST } from "@/lib/api";

// 2026-09 Workstream J Section 4 — the four-choice prompt for a follow-up
// task creation that collided with an ACTIVE duplicate (customer +
// opportunity + purpose + governing stage). Backed by the structured 409
// `POST /tasks` / `POST /ops/tasks` already return (`error: "duplicate_task"`,
// `existing_task`, `purpose_label`, `choices`) — this component never talks
// to the API itself; the caller re-submits its own create call with
// `duplicate_decision` (+ `duplicate_reason` for "keep both") once the
// employee picks, so the two creation endpoints stay the single source of
// truth for what each choice actually does.
export function DuplicateTaskDialog({ conflict, onChoose, onCancel, busy }) {
  const [reason, setReason] = useState("");
  if (!conflict) return null;
  const { existing_task: existing, purpose_label: purposeLabel } = conflict;
  const choose = (decision) => {
    if (decision === "keep_both" && reason.trim().length < 3) return;
    onChoose(decision, decision === "keep_both" ? reason.trim() : undefined);
  };
  return (
    <Dialog open={!!conflict} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="sm:max-w-md" data-testid="duplicate-task-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">An active task already covers this</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="text-muted-foreground">
            This customer already has an open task for the same purpose
            {purposeLabel ? <> (<b>{purposeLabel}</b>)</> : null}. Creating another would leave two active tasks
            for the same thing — pick how to handle it.
          </p>
          <div className="rounded-md border p-2.5 space-y-0.5" data-testid="duplicate-task-existing">
            <p className="font-medium">{existing?.title}</p>
            <p className="text-xs text-muted-foreground">
              Due {existing?.due_at ? fmtIST(existing.due_at) : "—"} · {existing?.created_by === "system" || existing?.created_by === "automation" ? "automation" : "manual"}
            </p>
          </div>
          <div className="grid gap-1.5">
            <Button size="sm" className="justify-start h-auto py-2" data-testid="duplicate-task-use-and-reschedule"
              disabled={busy} onClick={() => choose("use_and_reschedule")}>
              <span className="text-left">
                <span className="block font-medium">Use and reschedule existing task</span>
                <span className="block text-xs font-normal opacity-90">Recommended — updates the existing task's due date instead of creating a new one.</span>
              </span>
            </Button>
            <Button size="sm" variant="outline" className="justify-start h-auto py-2" data-testid="duplicate-task-replace"
              disabled={busy} onClick={() => choose("replace")}>
              <span className="text-left">
                <span className="block font-medium">Replace existing task</span>
                <span className="block text-xs font-normal text-muted-foreground">Supersedes the old task and creates the new one in its place.</span>
              </span>
            </Button>
            <div className="rounded-md border p-2 space-y-1.5" data-testid="duplicate-task-keep-both-block">
              <Button size="sm" variant="outline" className="justify-start h-auto py-2 w-full" data-testid="duplicate-task-keep-both"
                disabled={busy || reason.trim().length < 3} onClick={() => choose("keep_both")}>
                <span className="text-left">
                  <span className="block font-medium">Keep both</span>
                  <span className="block text-xs font-normal text-muted-foreground">Only if these are genuinely different commitments — say why.</span>
                </span>
              </Button>
              <Textarea data-testid="duplicate-task-keep-both-reason" rows={2} placeholder="Why are these two separate commitments? (required)"
                value={reason} onChange={(e) => setReason(e.target.value)} className="text-xs" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" size="sm" data-testid="duplicate-task-cancel" disabled={busy} onClick={onCancel}>Cancel</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Shared submit wrapper: calls `submit(extra)` (extra = {} normally, or
// {duplicate_decision, duplicate_reason} on a re-submit); on a 409
// `duplicate_task` it returns the conflict for the caller to show via
// <DuplicateTaskDialog>, instead of surfacing it as a generic error toast.
export async function submitWithDuplicateGuard(submit, extra = {}) {
  try {
    return { ok: true, data: await submit(extra) };
  } catch (e) {
    const d = e?.response?.data?.detail;
    if (e?.response?.status === 409 && d && typeof d === "object" && d.error === "duplicate_task") {
      return { ok: false, conflict: d };
    }
    throw e;
  }
}
